import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Photo } from '../../types';
import { ChevronLeft, ChevronRight, X, Grid } from 'lucide-react';

interface LightboxProps {
  photo: Photo | null;
  photos: Photo[];
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

export default function Lightbox({ photo, photos, onClose, onNext, onPrev }: LightboxProps) {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Prevent body scrolling and handle keyboard when lightbox is open
  useEffect(() => {
    if (photo) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      // Handle keyboard navigation
      const handleKeyDown = (e: KeyboardEvent) => {
        e.preventDefault();
        switch (e.key) {
          case 'Escape':
            onClose();
            break;
          case 'ArrowRight':
            if (onNext) onNext();
            break;
          case 'ArrowLeft':
            if (onPrev) onPrev();
            break;
        }
      };
      
      document.addEventListener('keydown', handleKeyDown);
      
      return () => {
        // Restore scroll position
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
        
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [photo, onClose, onNext, onPrev]);

  // Reset image loaded state when photo changes
  useEffect(() => {
    setImageLoaded(false);
  }, [photo]);

  if (!photo) return null;

  const currentIndex = photos.findIndex(p => p.id === photo.id);
  const hasMultiplePhotos = photos.length > 1;

  return createPortal(
    <div 
      onClick={onClose}
      className="fixed inset-0 z-[999999] bg-black bg-opacity-95 flex items-center justify-center"
      style={{ margin: 0, padding: 0 }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[999999] bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 sm:p-3 rounded-full transition-all duration-200"
        title="Fermer (Échap)"
      >
        <X size={20} className="sm:w-6 sm:h-6" />
      </button>

      {/* Navigation buttons */}
      {hasMultiplePhotos && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onPrev) onPrev();
            }}
            className="fixed left-4 sm:left-6 top-1/2 transform -translate-y-1/2 z-[999999] bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 sm:p-3 rounded-full transition-all duration-200"
            title="Photo précédente (←)"
          >
            <ChevronLeft size={24} className="sm:w-8 sm:h-8" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onNext) onNext();
            }}
            className="fixed right-4 sm:right-6 top-1/2 transform -translate-y-1/2 z-[999999] bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 sm:p-3 rounded-full transition-all duration-200"
            title="Photo suivante (→)"
          >
            <ChevronRight size={24} className="sm:w-8 sm:h-8" />
          </button>
        </>
      )}

      {/* Image container */}
      <div 
        className="relative w-full h-full flex items-center justify-center p-4 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading indicator */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        )}

        {/* Image */}
        <img
          src={photo.originalUrl}
          alt={photo.originalName}
          className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)}
        />

        {/* Photo info overlay */}
        <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white rounded-lg p-3 max-w-xs sm:max-w-md">
          <p className="text-sm sm:text-base font-medium truncate">{photo.originalName}</p>
          
          {/* Tags */}
          {photo.tags && photo.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {photo.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-600 text-white"
                >
                  {tag.name}
                </span>
              ))}
              {photo.tags.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-600 text-white">
                  +{photo.tags.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Group photo indicator */}
          {photo.isGroupPhoto && (
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-600 text-white">
                <Grid size={12} className="mr-1" />
                Photo de groupe
              </span>
            </div>
          )}

          {/* Counter */}
          {hasMultiplePhotos && (
            <p className="text-xs text-gray-300 mt-1">
              {currentIndex + 1} / {photos.length}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}